import React from "react";
import { ViewCustomer } from "collins-js-sdk";
import { User, Mail, Phone, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type CustomerCardProps = {
  customer: ViewCustomer;
  focused: boolean;
  onClick: () => void;
  onHover: () => void;
};

const CustomerCard = ({
  customer,
  focused,
  onClick,
  onHover
}: CustomerCardProps) => {
  const fullName =
    `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim();

  return (
    <button
      type="button"
      aria-pressed={focused}
      onClick={onClick}
      onMouseEnter={onHover}
      className={cn(
        "w-full text-left rounded-lg border-2 transition-all duration-200",
        "hover:border-primary hover:bg-accent/50 hover:shadow-sm",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "group relative overflow-hidden",
        focused
          ? "border-primary bg-accent/50 shadow-sm"
          : "border-border bg-card"
      )}
    >
      <div className="p-4 flex items-start gap-3">
        {/* Avatar */}
        <div
          className={cn(
            "flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center transition-colors",
            focused ? "bg-primary/20" : "bg-muted"
          )}
        >
          <User
            className={cn(
              "h-5 w-5 transition-colors",
              focused ? "text-primary" : "text-muted-foreground"
            )}
          />
        </div>

        {/* Customer Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-base truncate">
              {fullName || "Unknown Customer"}
            </h3>
          </div>

          <div className="space-y-1">
            {customer.email && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{customer.phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* Arrow Icon */}
        <ChevronRight
          className={cn(
            "h-5 w-5 flex-shrink-0 transition-all duration-200",
            focused
              ? "text-primary translate-x-0 opacity-100"
              : "text-muted-foreground -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
          )}
        />
      </div>

      {/* Subtle gradient overlay on hover */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-r from-transparent to-primary/5 opacity-0 transition-opacity",
          "group-hover:opacity-100"
        )}
      />
    </button>
  );
};

export default React.memo(CustomerCard);
